/**
 * One canonical shape for a phone number, everywhere.
 *
 * Numbers reach this system three ways — typed by a cashier, pasted out of
 * WhatsApp ("+62 811-6060-0518"), or imported from a spreadsheet ("081160600518",
 * sometimes with a stray apostrophe Excel added). Stored verbatim, those are
 * three different strings for one person: the duplicate check misses them, and
 * the WhatsApp dispatcher has to guess.
 *
 * Worse, nothing rejected a malformed number. Production holds
 * "+628116060600518" — sixteen digits, a fat-fingered duplication of "6060".
 * Meta accepted the send and answered "sent"; the message went nowhere, and
 * there is no bounce to tell anyone. A number that cannot be delivered to has
 * to be refused at the keyboard, because by dispatch time it looks fine.
 *
 * Canonical form is E.164: "+6281160600518". Unambiguous, sorts and compares as
 * itself, and is what every messaging API ultimately wants.
 */

/** Indonesian country code. The only one this business dials. */
const CC = '62';

/** National significant number: mobile is 8xx…, 9–12 digits in total. */
const MOBILE = /^8\d{8,11}$/;
/** Landlines and everything else on +62: area code then subscriber, 7–12. */
const OTHER_ID = /^[2-79]\d{6,11}$/;

export interface PhoneCheck {
  /** Canonical "+62…" form, or null when the input cannot be made into one. */
  e164: string | null;
  /** Why it was refused. Null when `e164` is set. */
  problem: string | null;
  /** WhatsApp only reaches mobile numbers; a landline is a valid contact but not a route. */
  isMobile: boolean;
}

/**
 * Parse anything a human might paste into canonical E.164.
 *
 * Accepts: +62 811-6060-0518, 62811..., 0811..., 811..., 0062811..., and the
 * same with spaces, dots, parentheses or a leading apostrophe from Excel.
 * Foreign numbers are kept as typed when they arrive with a country code.
 */
export function checkPhone(raw: string): PhoneCheck {
  const trimmed = (raw ?? '').trim().replace(/^'/, '');
  if (!trimmed) return { e164: null, problem: 'No telp wajib diisi', isMobile: false };

  // Two numbers in one field ("0812… / 0813…") is common in imported contact
  // lists. Stripping the punctuation would glue them into one 22-digit number
  // that looks like a typo, so say what it actually is — and refuse to pick
  // one, because which is the primary is the operator's call, not ours.
  if (/[/;]|\bat[au]u\b|,/.test(trimmed)) {
    return {
      e164: null,
      isMobile: false,
      problem: 'Ada lebih dari satu nomor di kolom ini. Simpan satu nomor utama saja.',
    };
  }

  const hadPlus = trimmed.startsWith('+') || trimmed.startsWith('00');
  let d = trimmed.replace(/\D/g, '');
  if (!d) return { e164: null, problem: 'No telp harus berisi angka', isMobile: false };

  // 00 is the old international prefix — 0062… is +62…
  if (d.startsWith('00')) d = d.slice(2);

  let national: string;
  if (d.startsWith(CC)) national = d.slice(CC.length);
  else if (d.startsWith('0')) national = d.replace(/^0+/, '');
  else if (hadPlus) {
    // A foreign number, given with its own country code. Keep it whole rather
    // than mangling it into +62 — it just cannot be reached on WhatsApp here.
    return d.length >= 8 && d.length <= 15
      ? { e164: `+${d}`, problem: null, isMobile: false }
      : { e164: null, problem: 'Nomor internasional tidak valid', isMobile: false };
  } else national = d;

  if (!national) return { e164: null, problem: 'No telp tidak lengkap', isMobile: false };

  if (MOBILE.test(national)) {
    return { e164: `+${CC}${national}`, problem: null, isMobile: true };
  }
  if (OTHER_ID.test(national)) {
    return { e164: `+${CC}${national}`, problem: null, isMobile: false };
  }

  // Say which way it is wrong; "invalid" alone sends people round in circles.
  if (national.startsWith('8')) {
    return {
      e164: null,
      isMobile: false,
      problem:
        national.length < 9
          ? `Nomor HP kependekan (${national.length} digit setelah +62). Contoh: 0811 6060 0518`
          : `Nomor HP kepanjangan (${national.length} digit setelah +62) — cek ada angka yang kedobelan.`,
    };
  }
  return {
    e164: null,
    isMobile: false,
    problem: 'Format no telp tidak dikenali. Contoh: 0811 6060 0518 atau +62 811 6060 0518',
  };
}

/** Canonical form, or null. Shorthand for `checkPhone(raw).e164`. */
export const normalizePhone = (raw: string): string | null => checkPhone(raw).e164;

/**
 * Comparison key — digits only, no plus. Two records match when this matches,
 * which is what the duplicate check and the import both need.
 */
export function phoneKey(raw: string): string {
  const e164 = normalizePhone(raw);
  if (e164) return e164.replace(/\D/g, '');
  // Unparseable numbers still need a stable key, or every bad row looks unique.
  return (raw ?? '').replace(/\D/g, '');
}

/** "+6281160600518" → "+62 811-6060-0518". Display only; never stored. */
export function formatPhone(raw: string): string {
  const e164 = normalizePhone(raw);
  if (!e164) return raw ?? '';
  if (!e164.startsWith(`+${CC}`)) return e164;
  const n = e164.slice(1 + CC.length);
  const groups =
    n.length >= 11
      ? [n.slice(0, 3), n.slice(3, 7), n.slice(7)]
      : [n.slice(0, 3), n.slice(3, 6), n.slice(6)];
  return `+${CC} ${groups.filter(Boolean).join('-')}`;
}
